// Утиліти для моніторингу пам'яті та відправки помилок

export interface MemoryInfo {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
    memoryUsage?: number; // У відсотках
}

export interface ErrorReport {
    type: 'memory_limit' | 'unknown_error' | 'upload_error';
    message: string;
    details?: any;
    memoryInfo?: MemoryInfo;
    timestamp: number;
    userAgent: string;
    galleryId?: number;
    fileName?: string;
    fileSize?: number;
    memoryMonitoringEnabled?: boolean; // Чи увімкнений моніторинг пам'яті для користувача
}

/**
 * Отримує інформацію про пам'ять браузера
 */
export function getMemoryInfo(): MemoryInfo | null {
    if ('memory' in performance) {
        const memory = (performance as any).memory;
        const memoryInfo: MemoryInfo = {
            usedJSHeapSize: memory.usedJSHeapSize,
            totalJSHeapSize: memory.totalJSHeapSize,
            jsHeapSizeLimit: memory.jsHeapSizeLimit,
        };
        
        // Обчислюємо відсоток використання
        memoryInfo.memoryUsage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
        
        return memoryInfo;
    }
    
    return null;
}

/**
 * Перевіряє чи перевищено ліміт пам'яті
 */
export function isMemoryLimitExceeded(threshold: number = 85): boolean {
    const memoryInfo = getMemoryInfo();
    return memoryInfo ? memoryInfo.memoryUsage! > threshold : false;
}

/**
 * Отримує розмір пам'яті в зручному форматі
 */
export function formatMemorySize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Bytes';
    
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Логує інформацію про пам'ять
 */
export function logMemoryUsage(context: string = ''): void {
    const memoryInfo = getMemoryInfo();
    if (memoryInfo) {
        console.log(`[MEMORY] ${context}:`, {
            used: formatMemorySize(memoryInfo.usedJSHeapSize),
            total: formatMemorySize(memoryInfo.totalJSHeapSize),
            limit: formatMemorySize(memoryInfo.jsHeapSizeLimit),
            usage: `${memoryInfo.memoryUsage!.toFixed(2)}%`
        });
    }
}

/**
 * Створює звіт про помилку
 */
export function createErrorReport(
    type: ErrorReport['type'],
    message: string,
    details?: any,
    context?: {
        galleryId?: number;
        fileName?: string;
        fileSize?: number;
    }
): ErrorReport {
    return {
        type,
        message,
        details,
        memoryInfo: getMemoryInfo() || undefined,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        ...context
    };
}
